// ABOUTME: Modal for confirming the placement option of an Amazon FBA replenishment.
// ABOUTME: Fetches placement options on open, lets the user pick one (or accept the cheapest preselection) and a pick date, then confirms the placement.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ShLabel from "../../theme/label/ShLabel";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import ShDateRangePicker from "../../theme/ShDateRangePicker";
import ShTextSkeleton from "../../theme/skeleton/ShTextSkeleton";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { yupRequired } from "../../../utils/validation";
import { useGetAmazonPlacementOptionsMutation, useConfirmAmazonPlacementMutation } from "../../../newapi/webshop/amazonreplenishmentSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";

const getOptionFee = (option) => {
    const fees = option?.fees ?? [];
    const total = fees.reduce((sum, fee) => sum + (fee?.value?.amount ?? 0), 0);
    const currency = fees.find(fee => fee?.value?.code)?.value?.code ?? "";
    return { total, currency };
};

const MConfirmAmazonPlacement = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [getAmazonPlacementOptions, { isLoading: optionsLoading }] = useGetAmazonPlacementOptionsMutation();
    const [confirmAmazonPlacement, { isLoading: confirmLoading }] = useConfirmAmazonPlacementMutation();

    const [options, setOptions] = useState([]);
    const [selectedOptionId, setSelectedOptionId] = useState("");
    const [pickDate, setPickDate] = useState(new Date());
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (!props.open || !props.replenishmentId) return;

        setOptions([]);
        setSelectedOptionId("");

        getAmazonPlacementOptions(props.replenishmentId).unwrap()
            .then((response) => {
                const fetchedOptions = response?.options ?? [];
                setOptions(fetchedOptions);

                if (fetchedOptions.length > 0) {
                    const cheapest = fetchedOptions.reduce((cheapestOption, option) => {
                        return getOptionFee(option).total < getOptionFee(cheapestOption).total ? option : cheapestOption;
                    }, fetchedOptions[0]);
                    setSelectedOptionId(cheapest.placementOptionId);
                }
            })
            .catch((err) => {
                showToastMessageRequestError(t("save_failed"), t, err);
            });
    }, [props.open, props.replenishmentId]);

    const handleClose = () => {
        setOptions([]);
        setSelectedOptionId("");
        setPickDate(new Date());
        setFieldErrors({});
        props.handleClose();
    };

    const handleDateChange = (date) => {
        setPickDate(date[0].formattedStartDate);
        setFieldErrors(prevErrors => ({ ...prevErrors, pickDate: '' }));
    };

    const rules = {
        placementOptionId: yupRequired('placement_options', t),
        pickDate: yupRequired('pick_date', t)
    };

    const schema = yup.object().shape(rules);

    const cheapestOptionId = options.length > 0
        ? options.reduce((cheapestOption, option) => {
            return getOptionFee(option).total < getOptionFee(cheapestOption).total ? option : cheapestOption;
        }, options[0]).placementOptionId
        : null;

    const confirm = async () => {
        try {
            if (confirmLoading) return;

            setFieldErrors({});
            await schema.validate({ placementOptionId: selectedOptionId, pickDate }, { abortEarly: false });

            const response = await confirmAmazonPlacement({
                id: props.replenishmentId,
                body: {
                    placementOptionId: selectedOptionId,
                    pickDate
                }
            }).unwrap();

            showToastMessagePostRequest(t, response);
            props.onConfirmed?.();
            handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessageRequestError(t("save_failed"), t, err);
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                }}
            >
                <Subheading>{t("placement_options")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    pb: 2,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: 400,
                    overflowY: "auto"
                }}
            >
                {optionsLoading && (
                    <>
                        <ShTextSkeleton />
                        <ShTextSkeleton />
                        <ShTextSkeleton />
                    </>
                )}

                {!optionsLoading && options.map((option) => {
                    const { total, currency } = getOptionFee(option);
                    const isSelected = selectedOptionId === option.placementOptionId;
                    const isCheapest = option.placementOptionId === cheapestOptionId;

                    return (
                        <Box
                            key={option.placementOptionId}
                            onClick={() => setSelectedOptionId(option.placementOptionId)}
                            sx={{
                                cursor: "pointer",
                                borderRadius: 4,
                                padding: 2,
                                border: `2px solid ${isSelected ? colors.blue[400] : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100])}`,
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2
                            }}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Text semibold>{option.shipmentIds?.length ?? 0} {t("shipments_in_option")}</Text>
                                    {isCheapest && (
                                        <ShLabel size={24} palette="normal" variant="green" fitted>
                                            {t("cheapest_option")}
                                        </ShLabel>
                                    )}
                                </Box>
                                <Text light>{t("placement_fee")}: {total} {currency}</Text>
                            </Box>
                        </Box>
                    );
                })}

                {fieldErrors.placementOptionId && (
                    <Text variant="red">{fieldErrors.placementOptionId}</Text>
                )}

                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7,
                        marginTop: 1
                    }}
                >
                    <Box sx={{ width: 120 }}>
                        <Text>{t("pick_date")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                            borderRadius: 4,
                            display: "flex",
                            height: 32
                        }}
                    >
                        <ShDateRangePicker
                            singleSelect={true}
                            startDate={pickDate}
                            select={handleDateChange}
                            size={32}
                        />
                    </Box>
                </Box>
                {fieldErrors.pickDate && (
                    <Text variant="red">{fieldErrors.pickDate}</Text>
                )}
            </Box>

            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={confirmLoading}
                        className={"group-btn"}
                        color={"green"}
                        block
                        variant="contained"
                        onClick={confirm}
                        disabled={!selectedOptionId || optionsLoading}
                    >
                        {t("confirm_placement")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MConfirmAmazonPlacement;
